import { Download } from "lucide-react";
import { toast } from "sonner";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { useLocale } from "@/hooks/useLocale";

/**
 * A compact "Install this tool" button for every tool's title row --
 * extends the same real mechanism FeaturedAppHero already used for
 * just the 6 original Premium Applications to all 220+ tools (see
 * scripts/generate-tool-manifests.mjs and usePwaManifest.ts for the
 * full explanation of what actually makes this install just THIS one
 * tool, not the whole site: a scoped manifest + narrow `scope` field,
 * already swapped in for every tool page via usePwaManifest).
 *
 * Directly requested as a marketing angle worth leaning into: nobody's
 * forced to install the whole 200+ tool site just to keep one tool
 * they actually use handy -- pin exactly the one tool to a taskbar,
 * Start menu, or home screen instead. And since this installs a
 * shortcut to the tool's real, live page rather than a separate
 * downloaded copy of its code, it keeps working and picks up every
 * future bug fix or improvement automatically, the same as visiting
 * the page normally would -- there's nothing here to go stale or need
 * a manual update.
 *
 * Gated on canPrompt (or iOS) -- deliberately, after an earlier
 * version of this button showed unconditionally and pointed to a
 * fallback message ("look for the install icon in your address bar")
 * that doesn't reliably correspond to anything actually there. Chrome
 * genuinely does not reliably offer beforeinstallprompt for a scoped,
 * per-tool manifest nested under the site's own root scope, even with
 * a fully correct manifest and an active service worker (confirmed via
 * Chrome/Firefox's own bug trackers, and by the fact that the site-wide
 * "Create Shortcut" button, whose manifest scope is the root "/", works
 * reliably where this one doesn't) -- that's a real Chrome limitation
 * for nested scopes specifically, not something fixable from this
 * codebase alone. A button pointing at UI that may not exist is worse
 * than no button; hiding here matches the same honest pattern
 * CreateShortcutButton already uses successfully for the site-wide
 * install.
 */
export function InstallToolButton({ toolName, className = "" }: { toolName: string; className?: string }) {
  const { canPrompt, isInstalled, isIOS, promptInstall } = useInstallPrompt();
  const { strings } = useLocale();
  const showButton = !isInstalled && (canPrompt || isIOS);

  if (!showButton) return null;

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isIOS) {
      // No programmatic install API on iOS Safari -- shows the real
      // manual steps instead of a button that would silently do
      // nothing, same honest handling as InstallSection.tsx.
      toast.info(strings.install.addToHomeScreenTitle, {
        description: strings.install.iosInstructions,
        duration: 9000,
      });
      return;
    }
    const outcome = await promptInstall();
    if (outcome === "accepted") {
      toast.success(`${toolName} installed! Look for its icon.`);
    }
  };

  return (
    <button
      onClick={handleClick}
      title={isIOS ? 'Open the Share menu and choose "Add to Home Screen"' : `Install ${toolName} as its own app`}
      aria-label={isIOS ? "Add to Home Screen" : `Install ${toolName} as its own app`}
      className={`inline-flex items-center justify-center min-h-11 min-w-11 max-sm:-mx-1.5 sm:min-h-0 sm:min-w-0 shrink-0 transition-colors text-muted-foreground hover:text-foreground ${className}`}
    >
      <Download className="h-4 w-4" />
    </button>
  );
}
